<template>
   <!-- [theme class css in cms-themes.scss] -->
   <div
      v-if="hasGenome"
      :data-cy="`snpNode-${allRsids[0].rsid}`"
      class="my-10 flex flex-col rounded-lg px-8 py-10 md:flex-row md:p-12 md:pr-24"
      :class="`theme-${cardColour}`"
   >
      <div class="mb-8 flex flex-row content-center items-start md:mr-8 md:flex-col">
         <IconNucleobase
            v-for="({ rsid, variant }, i) of allRsids"
            :key="rsid"
            :class="{ 'ml-2 md:ml-0 md:mt-5': i > 0 }"
            :variant="variant"
            :rsid="rsid"
         />
      </div>
      <div>
         <div v-for="(prediction, i) in state.predictions" :key="`section-${prediction.rsid}`" class="prediction">
            <!-- title - rsids & variants where snps are not missing (If there is no title, all snps are missing) -->
            <h4 v-if="state.titles[i]" class="text-bolder mb-3 text-lg">
               {{ state.titles[i] }}<span v-if="dataImputed.includes(prediction.rsid)">*</span>
            </h4>
            <!-- warning for any missing snps -->
            <p v-if="state.missing[i]">{{ state.missing[i] }}</p>
            <!-- prediction message or not-caused-by-missing-snp error message -->
            <div v-if="prediction.message" class="no-client-style" v-html="prediction.message"></div>
            <p v-else-if="!state.missing[i]">{{ $t("dna-testing.trait_reports.snp_imputation.error_imputation") }}</p>
         </div>

         <div v-if="dataImputed.length > 0" data-cy="imputed-info" class="sano-light mb-1 mt-4 leading-tight">
            <span class="flex flex-col text-base">{{
               $t("dna-testing.trait_reports.snp_imputation.message_imputed")
            }}</span>
            <LinkButton
               class="mt-10"
               size="sm"
               :color="cardColour"
               data-cy="more-about-imputation"
               target="_blank"
               url="/blog/what-is-imputation"
               :text="$t('dna-testing.trait_reports.snp_imputation.button_text')"
            />
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import type { ReportResp } from "@/data/api-layer/reportsApi";
import { toCamelCase } from "@/js/helpers/object.helper";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { ContainerTheme } from "@/types";

interface States {
   titles: null | string[];
   predictions: null | { message: string; rsid: string }[];
   missing: null | string[];
}

// note that Props cant be a seperate interface as the rich-text-schema file type is failing when importing this vue file
const props = defineProps<{
   rsids: string[];
   report: ReportResp;
   snpSources: { [rsids: string]: "genotype" | "imputation" };
   snps: { [rsids: string]: string };
   hasGenome: boolean;
}>();
const { t: $t } = useI18n();

const state = reactive<States>({
   titles: null,
   predictions: null,
   missing: null,
});

const allRsids = computed(() => {
   let splitRsids = props.rsids;
   if (props.rsids.length === 1) {
      // Transform ['rs145946881_rs41380347_rs4988235'] to ['rs145946881', 'rs41380347', 'rs4988235']
      // An entry in rsids like rs145946881_rs41380347_rs4988235 would be for an prediction like for
      // lactose tolerance where each combination of alleles at these positions has a different message
      // Where there are multiple entries in rsids, there will be multiple different messages in one section
      // (e.g. in the personality traits report - when bringing that over update this function)
      splitRsids = props.rsids[0].split("_");
   }
   return splitRsids.map((rsid) => ({ rsid: rsid.trim(), variant: props.snps[rsid.trim()] }));
});

const cardColour = computed(() => {
   return props.report?.cardContent?.color as ContainerTheme;
});

const getTitle = (splitRsids) => {
   let title = "";
   splitRsids.forEach((rsid, i) => {
      const variant = props.snps[rsid];

      if (variant && variant !== "--") {
         title += `${$t("dna-testing.trait_reports.snp_variant", { rsid: rsid, variant: variant })}${
            splitRsids.length - i > 1 ? "; " : "."
         }`;
      }
   });
   // Only return a title if at least one of the variants going towards making this message is not "--"
   return title.length > 1 ? title.charAt(0).toUpperCase() + title.slice(1) : null;
};

const getPrediction = (partialSnpInfo, rsid, splitRsids) => {
   let variant;
   if (splitRsids.length > 1) {
      // Where we have multiple rsids contributing to one outcome, we link the variants we got back for each
      // rsid into a string since this is what we will use to find the correct message, e.g. CCTTGG or CC_--GG
      variant = splitRsids.reduce((prevReduced, currRsid, i) => {
         // for example from 'rs145946881' to 'CC'
         const newVariant = props.snps[currRsid];
         const prevVariants = i === 1 ? props.snps[prevReduced] : prevReduced;
         return `${prevVariants}${newVariant === "--" ? "_" : ""}${newVariant}`;
      });
   } else {
      variant = props.snps[splitRsids[0]];
   }

   let message: string = "";

   const predictionData: string[] | string = partialSnpInfo[variant];
   if (Array.isArray(predictionData)) {
      // Where we have a lot of combinations with a multi-rsid prediction, we may have used a 'map' & array of keywords
      // to avoid repeating text e.g. {"--_CC_TT": ["Intolerant"]} map {"Intolerant": "Much longer text here"}
      if (partialSnpInfo.map) {
         predictionData.forEach((predictionKey) => {
            if (partialSnpInfo.map[predictionKey]) {
               message += partialSnpInfo.map[predictionKey];
            }
         });
      } else {
         console.error("prediction map is missing");
      }
   } else {
      message = predictionData;
   }

   return { message, rsid };
};

const getMissing = (splitRsids) => {
   const missingSnps = splitRsids.filter((rsid) => !props.snps[rsid] || props.snps[rsid] === "--");
   const allMissing = missingSnps.length === splitRsids.length;

   if (missingSnps.length === 1) {
      return $t("dna-testing.trait_reports.snp_imputation.error_one_snp_not_recorded", {
         position: missingSnps[0],
         optional_consequence: allMissing ? $t("dna-testing.trait_reports.snp_imputation.cannot_make_predictions") : "",
      });
   } else if (missingSnps.length > 1) {
      return $t("dna-testing.trait_reports.snp_imputation.error_many_snps_not_recorded", {
         positions: missingSnps.join(", "),
         optional_consequence: allMissing ? $t("dna-testing.trait_reports.snp_imputation.cannot_make_predictions") : "",
      });
   }
   return null;
};

const processData = () => {
   if (!props.hasGenome) return;
   const predictions = [];
   const titles = [];
   const missing = [];

   props.rsids.forEach((rsid, i) => {
      const thisRsid = rsid.trim();
      const partialSnpInfo = props.report.snpInfo[toCamelCase(thisRsid)];
      const splitRsids = thisRsid.split("_"); // split 'rs145946881_rs41380347_rs4988235' into ['rs145946881', 'rs41380347', 'rs4988235']

      if (!partialSnpInfo) {
         console.info(`report snpInfo is missing data for rsid ${thisRsid}`);
      } else {
         predictions.push(getPrediction(partialSnpInfo, thisRsid, splitRsids));
         titles.push(getTitle(splitRsids));
         missing.push(getMissing(splitRsids));
      }

      state.predictions = predictions;
      state.titles = titles;
      state.missing = missing;
   });
};

processData();

// If the data at one of the positions covered here has been imputed, we'll show a message to explain this
// (imputation will no longer happen on the participant site, but users who uploaded their data to the older site may have some imputed information)
const dataImputed = computed(() => {
   const imputedResults = [];

   for (let i = 0; i < allRsids.value.length; i++) {
      const thisRsid = allRsids.value[i].rsid;
      if (props.snpSources[thisRsid] === "imputation") imputedResults.push(thisRsid);
   }
   return imputedResults;
});
</script>

<style scoped lang="scss">
// remove marging added for the client portal
:deep(.no-client-style) {
   p {
      span {
         all: unset;
      }
   }
}

// Margin for cases with more than one snp per snp info section
.prediction + .prediction {
   margin-top: 1.5rem;
}
</style>
